/**
 * Letters. What the Board has written to you.
 *
 * Rule 10 makes communicating a decision in writing a duty. The system has
 * discharged it by email since the ledger was built, and email is a thing that
 * fails: an address typed wrong, a mailbox full, a provider that refuses, a
 * domain that cannot receive mail at all. In every one of those the duty was
 * still owed and the letter was still written. It has always been kept, exactly
 * as composed. It was only ever shown to officers.
 *
 * So this screen is not a copy of the applicant's post. It is the letter
 * itself, shown to the person it was addressed to, in the place they are
 * already signed in to. Somebody whose email never arrived can read what the
 * Board wrote by opening the application they filed.
 *
 * Which is why the screen says whether each letter was posted. A row that
 * quietly implied delivery would be worse than no screen: the reader would
 * think the office had reached them when it had not. Where a letter could not
 * be posted, the row says so and says why, and the letter is still there to
 * read.
 *
 * The body is the stored HTML in a sandboxed frame, not a re-render. Evidence
 * that changes when a template changes is not evidence.
 */

import { useCallback, useEffect, useState } from "react";
import { bi } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import { formatDateTime } from "../format.js";
import { statusHref } from "../router.js";
import { Callout, EmptyState, ErrorNote, Loading, Panel } from "../components/ui.js";

interface LetterHead {
  dedupeKey: string;
  kind: string;
  subjectId: string;
  subject: string;
  lang: string;
  status: string;
  posted: boolean;
  whyNotPosted: string | null;
  writtenAt: string;
  settledAt: string | null;
}

interface InboxResponse {
  address: string | null;
  letters: LetterHead[];
}

interface OneLetter {
  letter: LetterHead & { html: string; text: string };
}

/** What each kind of letter is, in the words an applicant would use. */
const KIND: Record<string, { en: string; ml: string }> = {
  applicant_stage: bi("About your application", "നിങ്ങളുടെ അപേക്ഷയെക്കുറിച്ച്"),
  daily_digest: bi("Your daily summary", "നിങ്ങളുടെ ദൈനംദിന സംഗ്രഹം"),
  department_referral: bi("Referred to a department", "ഒരു വകുപ്പിലേക്ക് അയച്ചു"),
  board_direction: bi("A direction of the Board", "ബോർഡിന്റെ ഒരു നിർദ്ദേശം"),
  test: bi("Test", "പരീക്ഷണം"),
};

/**
 * One letter, opened.
 *
 * Fetched when opened rather than with the list, because a hundred rendered
 * letters is a slow screen nobody reads, and because most of them will never be
 * opened.
 */
function Reading({ head, onClose }: { head: LetterHead; onClose: () => void }): JSX.Element {
  const { api } = useSession();
  const { t } = useLang();
  const [letter, setLetter] = useState<OneLetter["letter"] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const r = await api.get<OneLetter>(`/inbox/${encodeURIComponent(head.dedupeKey)}`);
        if (live) setLetter(r.letter);
      } catch (e) {
        if (live) setError(e);
      }
    })();
    return () => {
      live = false;
    };
  }, [api, head.dedupeKey]);

  return (
    <div className="letter-open">
      <div className="letter-open-head">
        <p className="letter-subject">{head.subject}</p>
        <button type="button" className="btn-quiet no-print" onClick={onClose}>
          {t(bi("Close", "അടയ്ക്കുക"))}
        </button>
      </div>
      {error ? <ErrorNote error={error} /> : null}
      {!letter && !error ? <Loading what={bi("Opening the letter", "കത്ത് തുറക്കുന്നു")} /> : null}
      {letter ? (
        <iframe
          className="letter-body"
          title={letter.subject}
          sandbox=""
          srcDoc={letter.html}
        />
      ) : null}
    </div>
  );
}

export function Letters(): JSX.Element {
  const { api } = useSession();
  const { lang, t } = useLang();
  const [data, setData] = useState<InboxResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<InboxResponse>("/inbox"));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const letters = data?.letters ?? [];
  const unposted = letters.filter((l) => !l.posted);

  return (
    <div className="stack">
      <h1>{t(bi("Letters", "കത്തുകൾ"))}</h1>
      <p className="lede">
        {t(
          bi(
            "Everything the Board has written to you, kept exactly as it was written. Whether or not it reached your email, it is here.",
            "ബോർഡ് നിങ്ങൾക്ക് എഴുതിയതെല്ലാം — എഴുതിയ രൂപത്തിൽത്തന്നെ സൂക്ഷിച്ചത്. നിങ്ങളുടെ ഇമെയിലിൽ എത്തിയാലും ഇല്ലെങ്കിലും, അത് ഇവിടെയുണ്ട്.",
          ),
        )}
      </p>

      {error ? <ErrorNote error={error} /> : null}
      {!data && !error ? <Loading what={bi("Fetching your letters", "നിങ്ങളുടെ കത്തുകൾ എടുക്കുന്നു")} /> : null}

      {unposted.length > 0 ? (
        <Callout
          tone="warn"
          title={bi(
            "Some of these were never posted to you",
            "ഇവയിൽ ചിലത് നിങ്ങൾക്ക് അയച്ചിട്ടില്ല",
          )}
          provenance={{
            kind: "statute",
            ref: "r.10",
            note: "The decision shall be communicated to the applicant in writing.",
          }}
        >
          <p style={{ marginBottom: 0 }}>
            {t(
              bi(
                "The Board owed you these letters and wrote them. The email did not go out, and each one says why below. Nothing about the letter changes because the post failed — it is the same letter, and you are reading it now.",
                "ഈ കത്തുകൾ ബോർഡ് നിങ്ങൾക്ക് നൽകേണ്ടതായിരുന്നു, എഴുതുകയും ചെയ്തു. ഇമെയിൽ പുറത്തുപോയില്ല; കാരണം ഓരോന്നിനും താഴെ പറയുന്നു. തപാൽ പരാജയപ്പെട്ടതുകൊണ്ട് കത്തിന് മാറ്റമില്ല — അതേ കത്താണ്, നിങ്ങൾ ഇപ്പോൾ അത് വായിക്കുന്നു.",
              ),
            )}
          </p>
        </Callout>
      ) : null}

      {data && letters.length === 0 ? (
        <EmptyState
          title={bi("No letters yet.", "ഇതുവരെ കത്തുകളൊന്നുമില്ല.")}
          body={bi(
            "The Board writes to you when your application moves: when it is received, when it is put before a board, when a question is raised, and when it is decided.",
            "നിങ്ങളുടെ അപേക്ഷ നീങ്ങുമ്പോൾ ബോർഡ് നിങ്ങൾക്ക് എഴുതും: ലഭിക്കുമ്പോൾ, ഒരു ബോർഡിന് മുന്നിൽ വയ്ക്കുമ്പോൾ, ഒരു ചോദ്യം ഉയരുമ്പോൾ, തീരുമാനമാകുമ്പോൾ.",
          )}
        />
      ) : null}

      {letters.length > 0 ? (
        <Panel>
          <ul className="letter-list">
            {letters.map((l) => (
              <li
                key={l.dedupeKey}
                className={l.posted ? "letter-row" : "letter-row letter-row-unposted"}
              >
                <div className="letter-row-head">
                  <span className="letter-kind">
                    {t(KIND[l.kind] ?? bi(l.kind, l.kind))}
                  </span>
                  <span className="letter-when">{formatDateTime(l.writtenAt, lang)}</span>
                </div>
                <p className="letter-subject">{l.subject}</p>
                {!l.posted ? (
                  <p className="letter-note">
                    {t(bi("Not posted", "അയച്ചിട്ടില്ല"))}
                    {l.whyNotPosted ? ` — ${l.whyNotPosted}` : ""}
                  </p>
                ) : null}
                <div className="letter-row-actions no-print">
                  <button
                    type="button"
                    className="btn-quiet"
                    onClick={() => setOpen(open === l.dedupeKey ? null : l.dedupeKey)}
                  >
                    {open === l.dedupeKey
                      ? t(bi("Close", "അടയ്ക്കുക"))
                      : t(bi("Read it", "വായിക്കുക"))}
                  </button>
                  {l.kind === "applicant_stage" || l.kind === "board_direction" ? (
                    <a className="btn-quiet" href={statusHref(l.subjectId)}>
                      {t(bi("Open the application", "അപേക്ഷ തുറക്കുക"))}
                    </a>
                  ) : null}
                </div>
                {open === l.dedupeKey ? (
                  <Reading head={l} onClose={() => setOpen(null)} />
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}
